// js/views/aktif-isler.ts — UWQ: Unified Work Queue / Aktif İşler (council fc8617a6).
// Endpoint: GET /api/v2/work-queue — council açık kararları + actionable onaylar + aktif issue'lar.
// Aksiyonlar GOVERNANCE-GATED: kartlardaki Onayla/Reddet, MEVCUT endpoint'lere gider
// (council resolve · approvals approve/reject) — auth + transition kuralları + executor orada.
// Issue kartları READ-ONLY (v1). Mevcut CSS sınıfları; renk-tek-başına yok (⚠/⏳/✓ ikon — a11y).

import { escHtml } from '../utils.js';
import { apiGetJson, apiFetch } from '../api.js';

interface WqAction { label: string; method: 'POST'; path: string; body?: Record<string, unknown>; confirm: string }
interface WqItem {
  kind: 'council' | 'approval' | 'issue' | 'audit';
  id: string; title: string; owner: string | null; risk: string | null;
  status: string; createdAt: string | null; needsYilmaz: boolean; actions: WqAction[];
  // Kart sözleşmesi alanları (crm-dashboard-work-queue §3) — eksik = null → '—' göster.
  stage?: string | null; blocker?: string | null; lastEvidence?: string | null;
  crmRef?: string | null; auditRef?: string | null;
}
interface WqResponse { ok: true; items: WqItem[]; counts: Record<string, number>; sourceErrors: string[] }

type ViewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; items: WqItem[]; counts: Record<string, number>; sourceErrors: string[] }
  | { kind: 'error'; message: string };

let state: ViewState = { kind: 'idle' };
let busy = false; // aksiyon sırasında çift tık koruması

const KIND_LABEL: Record<string, string> = {
  council: 'Council kararı',
  approval: 'Onay bekleyen',
  issue: 'Aktif iş',
  audit: 'Denetim sinyali',
};

// v2 — sidebar rozeti: Yılmaz-bekleyen kart sayısı (nav-aktif-isler üstünde).
function updateAktifIslerBadge(items: WqItem[]): void {
  const badge = document.getElementById('aktif-isler-badge');
  if (!badge) return;
  const n = items.filter((i) => i.needsYilmaz).length;
  if (n > 0) {
    badge.style.display = '';
    badge.textContent = String(n);
  } else {
    badge.style.display = 'none';
  }
}

export async function loadAktifIsler(): Promise<void> {
  state = { kind: 'loading' };
  const r = await apiGetJson<WqResponse>('/api/v2/work-queue');
  if (r.ok) {
    state = { kind: 'ready', items: r.data.items, counts: r.data.counts, sourceErrors: r.data.sourceErrors };
    updateAktifIslerBadge(r.data.items);
  } else state = { kind: 'error', message: r.message ?? r.error };
}

async function runAction(a: WqAction): Promise<void> {
  if (busy) return;
  // Governance gate (UI tarafı): açık onay sorusu — yanlışlıkla tık engellenir.
  if (!window.confirm(a.confirm)) return;
  busy = true;
  try {
    const res = await apiFetch(`/api/v2${a.path}`, {
      method: a.method,
      headers: { 'Content-Type': 'application/json' },
      body: a.body ? JSON.stringify(a.body) : undefined,
    });
    if (!res.ok) {
      const j = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      window.alert(`Aksiyon başarısız: ${j.error ?? res.status}${j.message ? ` — ${j.message}` : ''}`);
    }
  } finally {
    busy = false;
    await loadAktifIsler();
    renderAktifIsler();
  }
}

export function renderAktifIsler(): void {
  const root = document.getElementById('view-aktif-isler');
  if (!root) return;
  root.innerHTML = renderAktifIslerHtml(state);
  root.querySelectorAll<HTMLButtonElement>('[data-wq-idx]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (state.kind !== 'ready') return;
      const [i, j] = (btn.getAttribute('data-wq-idx') ?? '').split(':').map(Number);
      const action = state.items[i]?.actions[j];
      if (action) void runAction(action);
    });
  });
}

// Kart sözleşmesi alanı: değer varsa "etiket: değer", yoksa "etiket: —" (uydurma YOK).
function field(label: string, value: string | null | undefined): string {
  const v = value && value.length ? escHtml(value) : '—';
  return `<span class="muted">${escHtml(label)}: ${v}</span>`;
}

function card(it: WqItem, idx: number): string {
  const badge = it.needsYilmaz
    ? `<span class="gb-pill">⚠ Yılmaz bekliyor</span>`
    : `<span class="muted">⏳</span>`;
  // Blocker rozeti — engel VARSA görünür (renk-tek-başına değil: ⛔ ikon).
  const blockerBadge = it.blocker
    ? ` <span class="gb-pill">⛔ ${escHtml(it.blocker)}</span>`
    : '';
  // Kart sözleşmesi §3 — source/owner/stage/risk/last-evidence; eksik = '—'.
  const contractRow = [
    field('kaynak', KIND_LABEL[it.kind] ?? it.kind),
    field('sahip', it.owner),
    field('aşama', it.stage ?? it.status),
    field('risk', it.risk),
    field('son kanıt', it.lastEvidence ?? it.createdAt),
  ].join(' · ');
  // CRM / audit referansı (per-kart bağ — kolay olduğu yerde; içsel ref, dış link değil).
  const refRow = [
    field('CRM-ref', it.crmRef),
    field('audit-ref', it.auditRef),
  ].join(' · ');
  const buttons = it.actions
    .map((a, j) =>
      `<button type="button" class="sb-item" style="width:auto" data-wq-idx="${idx}:${j}">${escHtml(a.label)}</button>`)
    .join(' ');
  return `<li class="gb-card" style="margin:8px 0">
    <div>${badge} <b>${escHtml(it.title)}</b>${blockerBadge}</div>
    <div style="margin-top:4px">${contractRow}</div>
    <div style="margin-top:2px">${refRow}</div>
    ${buttons ? `<div style="margin-top:6px">${buttons}</div>` : ''}
  </li>`;
}

export function renderAktifIslerHtml(s: ViewState): string {
  const header = `
    <div class="view-header">
      <div>
        <div class="view-title">Aktif İşler</div>
        <div class="view-sub">Unified Work Queue — council + onaylar + aktif işler tek listede. Aksiyonlar governance-gated.</div>
      </div>
    </div>`;
  if (s.kind === 'loading' || s.kind === 'idle') {
    return header + `<div class="gb-card"><div class="gb-empty-sm">Yükleniyor…</div></div>`;
  }
  if (s.kind === 'error') {
    return header + `<div class="gb-card"><div class="gb-empty-sm">Liste alınamadı: ${escHtml(s.message)}</div></div>`;
  }
  const summary = `<div class="gb-card"><div class="gb-kpi-grid">
    <div><div class="gb-kpi-label">Council</div><div class="gb-kpi-value">${s.counts.council ?? 0}</div></div>
    <div><div class="gb-kpi-label">Onay</div><div class="gb-kpi-value">${s.counts.approval ?? 0}</div></div>
    <div><div class="gb-kpi-label">Aktif iş</div><div class="gb-kpi-value">${s.counts.issue ?? 0}</div></div>
    <div><div class="gb-kpi-label">Denetim</div><div class="gb-kpi-value">${s.counts.audit ?? 0}</div></div>
  </div></div>`;
  const errNote = s.sourceErrors.length
    ? `<div class="gb-card"><div class="muted">⚠ Bazı kaynaklar okunamadı: ${escHtml(s.sourceErrors.join(' · '))}</div></div>`
    : '';
  const list = s.items.length
    ? `<ul style="list-style:none;padding:0;margin:0">${s.items.map((it, i) => card(it, i)).join('')}</ul>`
    : `<div class="gb-card"><div class="gb-empty-sm">🎉 Bekleyen iş yok.</div></div>`;
  return header + summary + errNote + list;
}
